import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { MapPicker } from "@/components/MapPicker";
import { PostMaps } from "@/components/PostMaps";
import {
  Plus, Search, Heart, MessageCircle, ChevronDown, Send, Loader2, Users, BookHeart, Gift,
} from "lucide-react";

const CATEGORIES = ["Gospel Questions", "Parables", "Prayer Requests", "Faith & Life"];
const FILTERS = ["All", ...CATEGORIES];
const HERO = "https://images.unsplash.com/photo-1609234656388-0ff363383899?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwyfHxmZWxsb3dzaGlwJTIwZ3JvdXAlMjBjb2ZmZWUlMjBwcmF5ZXIlMjBkaXNjdXNzaW9uJTIwY29tbXVuaXR5fGVufDB8fHx8MTc4OTk1MjMxM3ww&ixlib=rb-4.1.0&q=85";

const timeAgo = (iso) => {
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return "just now";
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return `${Math.floor(d / 86400)}d ago`;
};

const catColor = (c) => ({
  "Gospel Questions": "bg-gold-light text-gold",
  "Parables": "bg-olive-light text-olive",
  "Prayer Requests": "bg-[#F5EFF3] text-ink",
  "Faith & Life": "bg-[#EFE7DA] text-ink",
}[c] || "bg-muted text-ink");

export default function Fellowship() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter !== "All") params.category = filter;
      if (search.trim()) params.search = search.trim();
      const { data } = await api.get("/fellowship/posts", { params });
      setPosts(data);
    } catch (e) {
      toast.error("Could not load discussions");
    } finally {
      setLoading(false);
    }
  }, [filter, search]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const amen = async (id) => {
    try {
      const { data } = await api.post(`/fellowship/posts/${id}/amen`);
      setPosts((p) => p.map((x) => (x.id === id ? data : x)));
    } catch {
      toast.error("Something went wrong");
    }
  };

  return (
    <div data-testid="fellowship-page">
      {/* Hero */}
      <section className="relative">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-14 pb-10 grid lg:grid-cols-2 gap-10 items-center">
          <div className="animate-rise">
            <span className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-gold mb-4">
              <Users className="w-4 h-4" /> Open to all — no account needed
            </span>
            <h1 className="font-serif text-4xl sm:text-5xl font-700 text-ink leading-tight">Fellowship Hall</h1>
            <p className="mt-4 text-ink/70 leading-relaxed max-w-lg">
              A place for brothers and sisters to ask questions and seek counsel from the Word.
              Post freely and encourage one another.
            </p>
          </div>
          <div className="hidden lg:block animate-fade-in">
            <img src={HERO} alt="Fellowship" className="rounded-2xl shadow-xl w-full aspect-[16/10] object-cover ring-1 ring-ink/10" />
          </div>
        </div>
      </section>

      {/* Request a free Bible */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 mb-8">
        <BibleRequest />
      </section>

      {/* Controls */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-col md:flex-row md:items-center gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-ink/40" />
            <Input
              data-testid="fellowship-search-input"
              placeholder="Search questions and discussions…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-11 h-12 rounded-full border-ink/15 bg-card"
            />
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="fellowship-ask-button" className="h-12 rounded-full bg-ink hover:bg-ink-dark text-parchment px-6 font-700">
                <Plus className="w-4 h-4 mr-1" /> Ask a Question
              </Button>
            </DialogTrigger>
            <AskDialog onDone={() => { setOpen(false); load(); }} />
          </Dialog>
        </div>

        <div className="flex flex-wrap gap-2 mb-8" data-testid="fellowship-category-filter">
          {FILTERS.map((f) => (
            <button
              key={f}
              data-testid={`fellowship-filter-${f.replace(/\s+/g, "-").toLowerCase()}`}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-full text-sm font-600 transition-colors ${
                filter === f ? "bg-gold text-ink" : "bg-card border border-border text-ink/70 hover:border-gold"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </section>

      {/* Threads */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-8">
        {loading ? (
          <div className="flex justify-center py-20 text-ink/40"><Loader2 className="w-8 h-8 animate-spin" /></div>
        ) : posts.length === 0 ? (
          <div className="text-center py-20 text-ink/50">
            <MessageCircle className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p>No discussions yet. Be the first to ask a question.</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="fellowship-thread-list">
            {posts.map((post) => (
              <Thread
                key={post.id}
                post={post}
                expanded={expanded === post.id}
                onToggle={() => setExpanded(expanded === post.id ? null : post.id)}
                onAmen={() => amen(post.id)}
                onReplied={(updated) => setPosts((p) => p.map((x) => (x.id === updated.id ? updated : x)))}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function Thread({ post, expanded, onToggle, onAmen, onReplied }) {
  const [body, setBody] = useState("");
  const [author, setAuthor] = useState("");
  const [sending, setSending] = useState(false);

  const submit = async () => {
    if (!body.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post(`/fellowship/posts/${post.id}/reply`, { body, author });
      onReplied(data);
      setBody("");
      toast.success("Reply posted");
    } catch {
      toast.error("Could not post reply");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-card rounded-2xl border border-border overflow-hidden hover:shadow-md transition-shadow" data-testid="fellowship-thread">
      <div className="p-6">
        <div className="flex items-center gap-3 mb-3">
          <Badge className={`${catColor(post.category)} border-0 font-600 rounded-full px-3`}>{post.category}</Badge>
          <span className="text-xs text-ink/40 font-mono">{timeAgo(post.created_at)}</span>
        </div>
        <h3 className="font-serif text-2xl font-600 text-ink leading-snug">{post.title}</h3>
        <p className="text-ink/70 mt-2 leading-relaxed">{post.body}</p>
        <PostMaps post={post} />
        <p className="text-xs text-olive mt-3 font-mono">— {post.author}</p>

        <div className="flex items-center gap-4 mt-5">
          <button
            onClick={onAmen}
            data-testid={`fellowship-amen-${post.id}`}
            className="inline-flex items-center gap-1.5 text-sm font-600 text-ink/70 hover:text-gold transition-colors"
          >
            <Heart className="w-4 h-4" /> Amen · {post.amens}
          </button>
          <button
            onClick={onToggle}
            data-testid={`fellowship-toggle-${post.id}`}
            className="inline-flex items-center gap-1.5 text-sm font-600 text-ink/70 hover:text-gold transition-colors"
          >
            <MessageCircle className="w-4 h-4" /> {post.replies.length} {post.replies.length === 1 ? "reply" : "replies"}
            <ChevronDown className={`w-4 h-4 transition-transform ${expanded ? "rotate-180" : ""}`} />
          </button>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-border bg-parchment/60 p-6 space-y-4 animate-fade-in">
          {post.replies.map((r) => (
            <div key={r.id} className="bg-card rounded-xl border border-border p-4">
              <p className="text-sm text-ink/80 leading-relaxed">{r.body}</p>
              <p className="text-xs text-olive mt-2 font-mono">— {r.author} · {timeAgo(r.created_at)}</p>
            </div>
          ))}

          <div className="space-y-3 pt-1">
            <Input
              placeholder="Your name (optional)"
              value={author}
              onChange={(e) => setAuthor(e.target.value)}
              className="bg-card border-ink/15"
              data-testid={`fellowship-reply-author-${post.id}`}
            />
            <Textarea
              placeholder="Share an encouraging, Scripture-rooted reply…"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              className="bg-card border-ink/15 min-h-[90px]"
              data-testid={`fellowship-reply-body-${post.id}`}
            />
            <Button
              onClick={submit}
              disabled={sending || !body.trim()}
              data-testid={`fellowship-reply-submit-${post.id}`}
              className="bg-gold hover:bg-ink text-ink hover:text-parchment font-700 rounded-full"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
              Post Reply
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function AskDialog({ onDone }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [author, setAuthor] = useState("");
  const [category, setCategory] = useState("Gospel Questions");
  const [maps, setMaps] = useState([]);
  const [sending, setSending] = useState(false);

  const submit = async () => {
    if (!title.trim() || !body.trim()) {
      toast.error("Please add a title and details");
      return;
    }
    setSending(true);
    try {
      await api.post("/fellowship/posts", { title, body, author, category, map_ids: maps.map((m) => m.id) });
      toast.success("Your question has been posted");
      setTitle(""); setBody(""); setAuthor(""); setMaps([]);
      onDone();
    } catch {
      toast.error("Could not post your question");
    } finally {
      setSending(false);
    }
  };

  return (
    <DialogContent data-testid="fellowship-post-modal" className="bg-parchment border-border max-w-lg max-h-[92vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="font-serif text-3xl font-600 text-ink">Ask the Fellowship</DialogTitle>
        <DialogDescription className="text-ink/60">
          Post openly — no account needed. Brothers and sisters will reply with encouragement rooted in Scripture.
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-4 mt-2">
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger data-testid="fellowship-post-category" className="bg-card border-ink/15 h-11">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input
          data-testid="fellowship-post-title"
          placeholder="Your question, in a sentence"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="bg-card border-ink/15 h-11"
        />
        <Textarea
          data-testid="fellowship-post-body"
          placeholder="Share more detail so others can help…"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          className="bg-card border-ink/15 min-h-[130px]"
        />
        <MapPicker text={`${title} ${body}`} selected={maps} onChange={setMaps} />
        <Input
          data-testid="fellowship-post-author"
          placeholder="Your name (optional — posts as 'A Brother in Christ')"
          value={author}
          onChange={(e) => setAuthor(e.target.value)}
          className="bg-card border-ink/15 h-11"
        />
      </div>
      <DialogFooter className="mt-4">
        <Button
          onClick={submit}
          disabled={sending}
          data-testid="fellowship-post-submit"
          className="w-full bg-ink hover:bg-ink-dark text-parchment font-700 rounded-full h-12"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
          Post to Fellowship Hall
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function BibleRequest() {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [translation, setTranslation] = useState("No preference");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);

  const submit = async () => {
    if (!name.trim() || !address.trim()) {
      toast.error("Please add your name and shipping address");
      return;
    }
    setSending(true);
    try {
      await api.post("/fellowship/bible-request", { name, email, address, translation, note });
      toast.success("Your Bible request has been received — we'll ship it to you soon, free of charge.");
      setName(""); setEmail(""); setAddress(""); setNote(""); setTranslation("No preference");
      setOpen(false);
    } catch {
      toast.error("Could not submit your request. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const TRANSLATIONS = [
    "No preference",
    "World English Bible (WEB)",
    "King James Version (KJV)",
    "English Standard Version (ESV)",
    "New International Version (NIV)",
  ];

  return (
    <div data-testid="bible-request-banner" className="relative overflow-hidden rounded-2xl bg-ink-deep text-parchment p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center gap-6">
      <div className="absolute -right-16 -top-16 w-56 h-56 rounded-full bg-gold/10 blur-3xl" />
      <div className="relative flex items-start gap-4 flex-1">
        <span className="flex items-center justify-center w-12 h-12 rounded-2xl bg-gold text-ink shrink-0">
          <BookHeart className="w-6 h-6" />
        </span>
        <div>
          <h3 className="font-serif text-2xl font-600">Need a Bible? We'll send you one — free.</h3>
          <p className="text-parchment/70 text-sm mt-1 leading-relaxed">
            If you don't own a Bible, request one here. We'll mail it to you and cover the shipping —
            no cost to you. "Freely you received; freely give." (Matthew 10:8)
          </p>
        </div>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button
            data-testid="bible-request-open"
            className="relative shrink-0 bg-gold hover:bg-parchment text-ink font-700 rounded-full h-12 px-6"
          >
            <Gift className="w-4 h-4 mr-2" /> Request a Free Bible
          </Button>
        </DialogTrigger>
        <DialogContent data-testid="bible-request-modal" className="bg-parchment border-border max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl font-600 text-ink">Request a Free Bible</DialogTitle>
            <DialogDescription className="text-ink/60">
              Tell us where to send it. We cover the shipping — it's our gift to you.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <Input
              data-testid="bible-request-name"
              placeholder="Your full name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="bg-card border-ink/15 h-11"
            />
            <Input
              data-testid="bible-request-email"
              placeholder="Email (optional — for delivery updates)"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-card border-ink/15 h-11"
            />
            <Textarea
              data-testid="bible-request-address"
              placeholder="Full shipping address (street, city, state/province, postal code, country)"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              className="bg-card border-ink/15 min-h-[90px]"
            />
            <Select value={translation} onValueChange={setTranslation}>
              <SelectTrigger data-testid="bible-request-translation" className="bg-card border-ink/15 h-11">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TRANSLATIONS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
            <Textarea
              data-testid="bible-request-note"
              placeholder="Anything we should know? (optional)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="bg-card border-ink/15 min-h-[70px]"
            />
          </div>
          <DialogFooter className="mt-4">
            <Button
              onClick={submit}
              disabled={sending}
              data-testid="bible-request-submit"
              className="w-full bg-ink hover:bg-ink-dark text-parchment font-700 rounded-full h-12"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Gift className="w-4 h-4 mr-2" />}
              Send Me a Bible
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

